// SR-PDF — assemble a doctrine-clean RepairPdfModel from the raw
// session + legs payload returned by GET /slip-repair-api/session/:id.
//
// The Slip Repair PDF lives in the browser (jsPDF). This module is
// the bridge: it takes the raw DB rows (which CAN contain agent
// names, CLV deltas, model_inputs, etc. in adjacent columns) and
// projects only the doctrine-safe surface into the PDF model.
//
// Critical: never read CLV magnitudes, fair_prob, or any A-\d agent
// reference into the model. Only verbatim Expert-Voice strings
// (`sharp_reason` / `safer_reason`) which are already clean.

import type {
  RepairPdfExcludedMarket,
  RepairPdfLeg,
  RepairPdfModel,
} from "./repairSharePdf.ts";
import type { LegRow, SessionRow } from "../pages/repair/useSlipRepair.ts";
import type { RepairTier } from "../pages/repair/_stubData.ts";
import type { LegAction } from "../pages/repair/components/LegCard.tsx";

// Shared with SlipRepairPage's adapter — humanizes long canonical
// market codes into shorter labels. Kept in-sync but local to avoid
// cross-importing UI page code from a non-page module.
const SHORT_MARKET: Record<string, string> = {
  match_winner_home: "Match Result · Home",
  match_winner_draw: "Match Result · Draw",
  match_winner_away: "Match Result · Away",
  double_chance_home_draw: "Double Chance · 1X",
  double_chance_home_away: "Double Chance · 12",
  double_chance_draw_away: "Double Chance · X2",
  double_chance_home_or_draw: "Double Chance · 1X",
  double_chance_home_or_away: "Double Chance · 12",
  double_chance_away_or_draw: "Double Chance · X2",
  both_teams_score_yes: "Both Teams to Score · Yes",
  both_teams_score_no: "Both Teams to Score · No",
  btts_yes: "Both Teams to Score · Yes",
  btts_no: "Both Teams to Score · No",
  total_goals_over_2_5: "Total Goals · Over 2.5",
  total_goals_under_2_5: "Total Goals · Under 2.5",
  total_goals_over_1_5: "Total Goals · Over 1.5",
  total_goals_under_1_5: "Total Goals · Under 1.5",
  total_goals_over_3_5: "Total Goals · Over 3.5",
  total_goals_under_3_5: "Total Goals · Under 3.5",
  asian_handicap_home_minus_0_5: "Asian Handicap · Home -0.5",
  asian_handicap_home_minus_1: "Asian Handicap · Home -1",
  asian_handicap_home_minus_1_5: "Asian Handicap · Home -1.5",
  asian_handicap_home_plus_0_5: "Asian Handicap · Home +0.5",
  asian_handicap_home_plus_1: "Asian Handicap · Home +1",
  asian_handicap_home_plus_1_5: "Asian Handicap · Home +1.5",
  asian_handicap_away_minus_0_5: "Asian Handicap · Away -0.5",
  asian_handicap_away_minus_1: "Asian Handicap · Away -1",
  asian_handicap_away_minus_1_5: "Asian Handicap · Away -1.5",
  asian_handicap_away_plus_0_5: "Asian Handicap · Away +0.5",
  asian_handicap_away_plus_1: "Asian Handicap · Away +1",
  asian_handicap_away_plus_1_5: "Asian Handicap · Away +1.5",
  // Substrate short-form codes (market_selection joined)
  ou_over_0_5: "Total Goals · Over 0.5",
  ou_over_1_5: "Total Goals · Over 1.5",
  ou_over_2_5: "Total Goals · Over 2.5",
  ou_over_3_5: "Total Goals · Over 3.5",
  ou_over_4_5: "Total Goals · Over 4.5",
  ou_under_0_5: "Total Goals · Under 0.5",
  ou_under_1_5: "Total Goals · Under 1.5",
  ou_under_2_5: "Total Goals · Under 2.5",
  ou_under_3_5: "Total Goals · Under 3.5",
  ou_under_4_5: "Total Goals · Under 4.5",
};

function humanizeCode(code: string): string {
  return code
    .split("_")
    .map((p) => (p.length === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
    .join(" ");
}

export function displayMarket(code: string | null): string {
  if (!code) return "—";
  return SHORT_MARKET[code] ?? humanizeCode(code);
}

function pickDecision(row: LegRow, mode: "sharp" | "safer" | "both"): RepairPdfLeg["decision"] {
  if (mode === "safer") return (row.safer_decision ?? row.sharp_decision ?? "RETAIN");
  if (mode === "sharp") return (row.sharp_decision ?? row.safer_decision ?? "RETAIN");
  // both: prefer the strongest signal
  const s = row.sharp_decision;
  const sf = row.safer_decision;
  const rank = (d: typeof s): number => {
    switch (d) {
      case "REPLACE": return 5;
      case "MARGINAL": return 4;
      case "FLAG": return 3;
      case "UNRESOLVED": return 2;
      case "RETAIN": return 1;
      default: return 0;
    }
  };
  return (rank(s) >= rank(sf) ? (s ?? "RETAIN") : (sf ?? "RETAIN")) as RepairPdfLeg["decision"];
}

function pickReplacement(row: LegRow, mode: "sharp" | "safer" | "both"): {
  market: string | null;
  selection: string | null;
  odds: number | null;
} {
  const sharpSet = row.sharp_rep_selection !== null && row.sharp_rep_odds !== null;
  const saferSet = row.safer_rep_selection !== null && row.safer_rep_odds !== null;
  if (mode === "safer" && saferSet) {
    return { market: displayMarket(row.safer_rep_market), selection: row.safer_rep_selection, odds: row.safer_rep_odds };
  }
  if (mode === "sharp" && sharpSet) {
    return { market: displayMarket(row.sharp_rep_market), selection: row.sharp_rep_selection, odds: row.sharp_rep_odds };
  }
  if (sharpSet) {
    return { market: displayMarket(row.sharp_rep_market), selection: row.sharp_rep_selection, odds: row.sharp_rep_odds };
  }
  if (saferSet) {
    return { market: displayMarket(row.safer_rep_market), selection: row.safer_rep_selection, odds: row.safer_rep_odds };
  }
  return { market: null, selection: null, odds: null };
}

function pickReason(row: LegRow, mode: "sharp" | "safer" | "both"): string {
  if (mode === "safer") return (row.safer_reason ?? row.sharp_reason ?? "").trim();
  return (row.sharp_reason ?? row.safer_reason ?? "").trim();
}

export interface BuildModelInput {
  session: SessionRow;
  legs: LegRow[];
  mode?: "sharp" | "safer" | "both";
  /** SR-TIERS — the user's chosen tier on the results screen.
   *  When present, drives per-leg display + combined-odds total. */
  tier?: RepairTier;
  /** Per-leg user actions (accept / override / keep / remove). When
   *  omitted, every leg is treated as un-touched (default: pick the
   *  tier replacement if any, else the original). */
  legActions?: Record<string, LegAction>;
  /** Site origin for the share URL (e.g. "https://strikza.app"). */
  origin?: string;
  /** Share token issued by POST /share. Embedded into shareUrl. */
  shareToken?: string;
  referralCode?: string | null;
  ownerHandle?: string;
  excludedMarkets?: RepairPdfExcludedMarket[];
}

// Tier picker: returns a clean per-tier { market, selection, odds, decision }
// projection from the legacy sharp/safer columns + tier_picks JSON.
function tierPick(row: LegRow, tier: RepairTier): {
  decision: RepairPdfLeg["decision"];
  market: string | null;
  selection: string | null;
  odds: number | null;
} {
  const tp = row.tier_picks?.[tier];
  if (tp && (tp.decision === "REPLACE" || tp.decision === "MARGINAL")) {
    const sel = tp.selection === tp.market ? null : tp.selection;
    return {
      decision: tp.decision,
      market: displayMarket(tp.market),
      selection: sel,
      odds: tp.odds,
    };
  }
  if (tp && tp.decision === "RETAIN") {
    const sel = tp.selection === tp.market ? null : (tp.selection ? String(tp.selection) : null);
    return {
      decision: "RETAIN",
      market: tp.market ? displayMarket(tp.market) : null,
      selection: sel,
      odds: tp.odds ?? null,
    };
  }
  if (tp && (tp.decision === "FLAG" || tp.decision === "UNRESOLVED")) {
    return { decision: tp.decision, market: null, selection: null, odds: null };
  }
  // Fallback to legacy sharp/safer columns.
  const legacyMode = tier === "bold" || tier === "moderate" ? "sharp" : "safer";
  const decision = pickDecision(row, legacyMode);
  const rep = pickReplacement(row, legacyMode);
  return { decision, market: rep.market, selection: rep.selection, odds: rep.odds };
}

// Suppress duplicate trailing tokens like "Both Teams to Score · Yes · yes"
// where the selection is just the lowercase tail of the market label. The
// upstream selector emits the raw substrate selection alongside the
// humanized market — readers don't need both.
function cleanSelection(market: string | null, selection: string | null): string | null {
  if (!selection) return null;
  if (!market) return selection;
  const lastSegment = market.split("·").pop()?.trim().toLowerCase() ?? "";
  if (lastSegment && lastSegment === selection.trim().toLowerCase()) return null;
  // Strip the raw_code form like "away_plus_1_5" when it duplicates
  // already-humanized text in the market label.
  if (/^[a-z][a-z0-9_]+$/.test(selection) && market.toLowerCase().includes(selection.replace(/_/g, " "))) {
    return null;
  }
  return selection;
}

export function buildRepairPdfModel(input: BuildModelInput): RepairPdfModel {
  const { session, legs } = input;
  const mode = input.mode ?? session.mode_selected ?? "both";
  const tier: RepairTier = input.tier ?? "moderate";
  const legActions = input.legActions ?? {};
  const origin = (input.origin ?? "https://strikza.app").replace(/\/+$/, "");
  const sharePath = input.shareToken
    ? `/r/${input.shareToken}`
    : `/repair?session=${session.session_id}`;
  const refSuffix = input.referralCode ? `?ref=${input.referralCode}` : "";
  const shareUrl = `${origin}${sharePath}${refSuffix}`;

  const slipCode = session.session_id.slice(0, 12).toUpperCase();

  // Resolve each leg to an *active* state — what the user is going to
  // actually take to the bookmaker. The PDF should mirror that exactly.
  let comboOdds = 1;
  let anyOdds = false;
  const pdfLegs: RepairPdfLeg[] = legs
    .slice()
    .sort((a, b) => a.leg_number - b.leg_number)
    .filter((row) => legActions[row.leg_id] !== "remove")
    .map((row) => {
      const pick = tierPick(row, tier);
      const action = legActions[row.leg_id] ?? null;
      const tierProposesReplacement = (pick.decision === "REPLACE" || pick.decision === "MARGINAL") &&
        pick.odds != null && pick.market != null && pick.selection != null;

      // Active decision rules:
      // - override → user keeps original; show RETAIN, no replacement line
      // - accept (or untouched on REPLACE) → tier replacement IS the leg;
      //   show REPLACE with replacement as primary
      // - untouched on RETAIN/FLAG/UNRESOLVED → original is the leg
      let activeDecision: RepairPdfLeg["decision"];
      let activeMarket: string;
      let activeSelection: string;
      let activeOdds: number | null;
      let altMarket: string | null = null;
      let altSelection: string | null = null;
      let altOdds: number | null = null;

      if (action === "override" || !tierProposesReplacement) {
        // Original wins.
        activeDecision = pick.decision === "RETAIN" ? "RETAIN" : pick.decision;
        activeMarket = row.orig_market ?? "—";
        activeSelection = row.orig_selection ?? "—";
        activeOdds = row.orig_odds;
        // Surface the tier's suggestion as the alternative — even on RETAIN.
        if (pick.market && pick.odds) {
          altMarket = pick.market;
          altSelection = cleanSelection(pick.market, pick.selection);
          altOdds = pick.odds;
        }
      } else {
        // Replacement IS the active leg (default behavior + accept).
        activeDecision = "REPLACE";
        activeMarket = pick.market!;
        activeSelection = cleanSelection(pick.market, pick.selection) ?? "";
        activeOdds = pick.odds;
        // Surface original as the (replaced) was-line.
        altMarket = row.orig_market ?? null;
        altSelection = row.orig_selection ?? null;
        altOdds = row.orig_odds;
      }

      // v3 upgrade: for UNRESOLVED legs with a blind-guidance upgrade,
      // surface the upgrade as a suggestion in the PDF.
      if (activeDecision === "UNRESOLVED" && (row as any).upgrade_market && (row as any).upgrade_odds) {
        altMarket = (row as any).upgrade_selection ?? (row as any).upgrade_market;
        altSelection = null;
        altOdds = (row as any).upgrade_odds;
      }

      if (activeOdds && activeOdds > 0) {
        comboOdds *= activeOdds;
        anyOdds = true;
      }

      const match = (row.orig_match ?? "").trim() || "—";
      const reasonFromTier = row.tier_picks?.[tier]?.reason ?? null;
      const upgradeReason = (row as any).upgrade_reason as string | null;
      const reasoning = (upgradeReason && activeDecision === "UNRESOLVED"
        ? upgradeReason
        : (reasonFromTier ?? pickReason(row, mode))
      ).trim();

      return {
        n: row.leg_number,
        match,
        decision: activeDecision,
        voidRisk: row.void_risk,
        // PDF "ORIGINAL" line shows the active leg now (post user action).
        originalMarket: activeMarket,
        originalSelection: activeSelection,
        originalOdds: activeOdds,
        // PDF "STRIKZA SUGGESTS / was" line shows the alternative.
        replacementMarket: altMarket ? displayMarket(altMarket) : null,
        replacementSelection: altSelection,
        replacementOdds: altOdds,
        reasoning,
      };
    });

  // Tier total — prefer server-computed, override with client recompute
  // when the user has touched any leg (matches RepairHero recompute).
  const serverTierTotal: number | null =
    tier === "bold" ? session.total_odds_bold ?? session.total_odds_sharp
    : tier === "moderate" ? session.total_odds_moderate
    : tier === "safer" ? session.total_odds_safer_tier ?? session.total_odds_safer
    : session.total_odds_conservative;
  const hasAnyAction = Object.values(legActions).some((a) => a !== null && a !== undefined);
  const tierFinalOdds: number | null = hasAnyAction
    ? (anyOdds ? comboOdds : serverTierTotal)
    : serverTierTotal;

  return {
    slipCode,
    bookmaker: session.bookmaker ?? "—",
    repairedAt: session.repaired_at,
    legCount: pdfLegs.length,
    healthBefore: session.health_score_before,
    healthAfter: session.health_score_after,
    originalOdds: session.total_odds_original,
    // SR-TIERS — sharpOdds slot now carries the active tier total.
    // saferOdds left null (the PDF cover no longer renders a pair).
    sharpOdds: tierFinalOdds,
    saferOdds: null,
    voidHighBefore: session.void_high_before ?? 0,
    voidHighAfter: session.void_high_after ?? 0,
    slipSummary: session.slip_summary,
    legs: pdfLegs,
    excludedMarkets: input.excludedMarkets ?? [],
    shareUrl,
    referralCode: input.referralCode ?? null,
    ownerHandle: input.ownerHandle ?? "this user",
  };
}
